import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  ArrowLeft, FileText, IndianRupee, TrendingUp, Clock, AlertTriangle,
  Loader2, Building2, Calendar, CheckCircle2, Send, Plus, Inbox, Filter, XCircle,
  Shield, AlertCircle, ArrowUpCircle, ExternalLink, Download, MessageSquare, Flag
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (a) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(a || 0);

const HEALTH_CONFIG = {
  healthy: { label: "Healthy", color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", dot: "bg-emerald-500" },
  needs_attention: { label: "Needs Attention", color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", dot: "bg-amber-500" },
  critical: { label: "Critical", color: "text-red-700", bg: "bg-red-50", border: "border-red-200", dot: "bg-red-500" },
  awaiting_docs: { label: "Awaiting Docs", color: "text-orange-700", bg: "bg-orange-50", border: "border-orange-200", dot: "bg-orange-500" },
  upgrade_recommended: { label: "Upgrade Recommended", color: "text-purple-700", bg: "bg-purple-50", border: "border-purple-200", dot: "bg-purple-500" },
};

function HealthBadge({ status, reasons }) {
  const cfg = HEALTH_CONFIG[status] || HEALTH_CONFIG.healthy;
  return (
    <div className="flex flex-col gap-1">
      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${cfg.bg} ${cfg.color} border ${cfg.border} w-fit`} data-testid={`health-badge-${status}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
        {cfg.label}
      </span>
      {reasons?.length > 0 && status !== "healthy" && (
        <p className="text-[11px] text-slate-500 pl-1">{reasons.join(" | ")}</p>
      )}
    </div>
  );
}

export default function CAClientDetailPage() {
  const { clientId } = useParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [gstData, setGstData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [showDocReq, setShowDocReq] = useState(false);
  const [docTitle, setDocTitle] = useState("");
  const [docMonth, setDocMonth] = useState("");
  const [docDesc, setDocDesc] = useState("");
  const [docPriority, setDocPriority] = useState("normal");
  const [docDueDate, setDocDueDate] = useState("");
  const [sending, setSending] = useState(false);
  const [docRequests, setDocRequests] = useState([]);
  const [gstAlerts, setGstAlerts] = useState([]);

  // Invoice filters
  const [invTypeFilter, setInvTypeFilter] = useState("all");
  const [invMonthFilter, setInvMonthFilter] = useState("");
  const [invPaymentFilter, setInvPaymentFilter] = useState("all");
  const [filingLoading, setFilingLoading] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) { navigate("/auth"); return; }
    if (isAuthenticated && clientId) fetchData();
  }, [isAuthenticated, authLoading, clientId]);

  const fetchData = async () => {
    try {
      const [ovRes, gstRes, docRes] = await Promise.all([
        axios.get(`${API}/ca/clients/${clientId}/overview`, { withCredentials: true }),
        axios.get(`${API}/ca/clients/${clientId}/gst`, { withCredentials: true }),
        axios.get(`${API}/ca/document-requests`, { withCredentials: true }),
      ]);
      setOverview(ovRes.data);
      setGstData(gstRes.data.gst_data || []);
      setGstAlerts(gstRes.data.alerts || []);
      // Filter doc requests for this client
      const allDocs = docRes.data.requests || [];
      setDocRequests(allDocs.filter(d => d.client_user_id === clientId));
      fetchInvoices();
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("No access to this client");
        navigate("/ca/dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      const params = {};
      if (invTypeFilter !== "all") params.invoice_type = invTypeFilter;
      if (invMonthFilter) params.month = invMonthFilter;
      if (invPaymentFilter !== "all") params.payment_status = invPaymentFilter;
      const invRes = await axios.get(`${API}/ca/clients/${clientId}/invoices`, { params, withCredentials: true });
      setInvoices(invRes.data.invoices || []);
    } catch {}
  };

  useEffect(() => {
    if (isAuthenticated && clientId && !loading) fetchInvoices();
  }, [invTypeFilter, invMonthFilter, invPaymentFilter]);

  const handleMarkFiled = async (month) => {
    setFilingLoading(month);
    try {
      const res = await axios.put(`${API}/ca/clients/${clientId}/gst/${month}/mark-filed`, {}, { withCredentials: true });
      toast.success(res.data.filed ? `GST for ${month} marked as filed` : `GST for ${month} unmarked`);
      const gstRes = await axios.get(`${API}/ca/clients/${clientId}/gst`, { withCredentials: true });
      setGstData(gstRes.data.gst_data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setFilingLoading(null);
    }
  };

  const handleDocRequest = async () => {
    if (!docTitle.trim()) { toast.error("Enter a title"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/ca/document-requests`, {
        client_user_id: clientId,
        title: docTitle,
        description: docDesc,
        month: docMonth,
        priority: docPriority,
        due_date: docDueDate,
      }, { withCredentials: true });
      toast.success("Document request sent!");
      setShowDocReq(false);
      setDocTitle(""); setDocMonth(""); setDocDesc(""); setDocPriority("normal"); setDocDueDate("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSending(false);
    }
  };

  const handleStatusUpdate = async (requestId, newStatus) => {
    try {
      await axios.put(`${API}/ca/document-requests/${requestId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  if (loading || authLoading) {
    return <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#7C3AED]" /></div>;
  }

  if (!overview) {
    return <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center"><p className="text-slate-500">Client not found</p></div>;
  }

  const client = overview.client;
  const managedBy = overview.managed_by;
  const healthStatus = overview.health_status;
  const healthReasons = overview.health_reasons;

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "invoices", label: `Invoices (${overview.total_invoices})` },
    { id: "gst", label: "GST" },
    { id: "documents", label: "Documents" },
  ];

  const statusBadge = (s) => ({
    paid: "bg-emerald-50 text-emerald-700",
    partial: "bg-amber-50 text-amber-700",
    unpaid: "bg-red-50 text-red-700",
  }[s] || "bg-slate-50 text-slate-600");

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-6 px-4" data-testid="ca-client-detail-page">
      <div className="max-w-6xl mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center gap-3 mb-5">
          <Link to="/ca/dashboard" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <span className="text-slate-300">/</span>
          <span className="text-sm font-medium text-slate-900">{client.name}</span>
        </div>

        {/* Client Header */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5 mb-6" data-testid="client-header">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-violet-50 flex items-center justify-center text-violet-600 font-bold text-xl flex-shrink-0">
              {client.name?.charAt(0) || "?"}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-xl font-bold text-slate-900">{client.name}</h1>
                {healthStatus && <HealthBadge status={healthStatus} reasons={healthReasons} />}
              </div>
              <p className="text-sm text-slate-500 mt-0.5">{client.email}</p>
              {overview.companies?.[0]?.gstin && (
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded font-mono mt-1 inline-block">{overview.companies[0].gstin}</span>
              )}
            </div>
            <Button onClick={() => setShowDocReq(true)} variant="outline" className="text-sm border-violet-200 text-violet-700 hover:bg-violet-50" data-testid="request-docs-btn">
              <Send className="w-4 h-4 mr-1.5" /> Request Documents
            </Button>
          </div>

          {/* Managed By Info */}
          {managedBy && managedBy.professional_id && (
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2" data-testid="managed-by-info">
              <Shield className="w-4 h-4 text-violet-500" />
              <p className="text-xs text-slate-500">
                Managed by <span className="font-semibold text-slate-700">{managedBy.name}</span>
                {managedBy.firm_name && <span> ({managedBy.firm_name})</span>}
                {" | "}
                <Link to={`/pro/${managedBy.professional_id}`} className="text-violet-600 hover:underline font-mono font-semibold" data-testid="managed-by-link">
                  {managedBy.professional_id} <ExternalLink className="w-3 h-3 inline" />
                </Link>
              </p>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 mb-5 overflow-x-auto" data-testid="client-tabs">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`} data-testid={`client-tab-${tab.id}`}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="client-overview-stats">
              {[
                { label: "Total Sales", value: fmt(overview.total_sales), icon: TrendingUp, color: "text-emerald-600", bg: "bg-emerald-50" },
                { label: "Monthly Sales", value: fmt(overview.monthly_sales), icon: Calendar, color: "text-blue-600", bg: "bg-blue-50" },
                { label: "Receivable", value: fmt(overview.total_receivable), icon: IndianRupee, color: "text-violet-600", bg: "bg-violet-50" },
                { label: "Overdue", value: overview.overdue_invoices, icon: AlertTriangle, color: overview.overdue_invoices > 0 ? "text-red-600" : "text-slate-400", bg: overview.overdue_invoices > 0 ? "bg-red-50" : "bg-slate-50" },
                { label: "GST Collected", value: fmt(overview.gst_collected), icon: IndianRupee, color: "text-teal-600", bg: "bg-teal-50" },
                { label: "GST Paid", value: fmt(overview.gst_paid), icon: IndianRupee, color: "text-orange-600", bg: "bg-orange-50" },
                { label: "Net GST", value: fmt(overview.net_gst), icon: IndianRupee, color: "text-indigo-600", bg: "bg-indigo-50" },
                { label: "Pending", value: overview.pending_invoices, icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
              ].map((card, i) => (
                <motion.div key={card.label} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                  className="bg-white rounded-xl border border-slate-100 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-7 h-7 rounded-lg ${card.bg} flex items-center justify-center`}><card.icon className={`w-3.5 h-3.5 ${card.color}`} /></div>
                    <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">{card.label}</span>
                  </div>
                  <p className="text-lg font-bold text-slate-900">{card.value}</p>
                </motion.div>
              ))}
            </div>

            {/* Recent Invoices */}
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="client-recent-invoices">
              <div className="p-4 border-b border-slate-100">
                <h3 className="text-sm font-semibold text-slate-900">Recent Invoices</h3>
              </div>
              <div className="divide-y divide-slate-50">
                {(overview.recent_invoices || []).map(inv => (
                  <div key={inv.invoice_id} className="flex items-center gap-3 px-4 py-3" data-testid={`client-inv-${inv.invoice_id}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${inv.invoice_type === "sales" ? "bg-blue-50 text-blue-600" : "bg-purple-50 text-purple-600"}`}>
                      <FileText className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{inv.customer_name}</p>
                      <p className="text-xs text-slate-500">{inv.invoice_number} &middot; {inv.invoice_date}</p>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusBadge(inv.payment_status)}`}>{inv.payment_status}</span>
                    <span className="text-sm font-bold text-slate-900">{fmt(inv.grand_total)}</span>
                  </div>
                ))}
                {(!overview.recent_invoices || overview.recent_invoices.length === 0) && (
                  <div className="p-6 text-center text-slate-400 text-sm">No invoices yet</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === "invoices" && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 flex flex-wrap items-center gap-3" data-testid="invoice-filters">
              <Filter className="w-4 h-4 text-slate-400" />
              <div className="flex gap-1">
                {[
                  { value: "all", label: "All Types" },
                  { value: "sales", label: "Sales" },
                  { value: "purchase", label: "Purchase" },
                ].map(f => (
                  <button key={f.value} onClick={() => setInvTypeFilter(f.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      invTypeFilter === f.value ? "bg-violet-100 text-violet-700" : "bg-slate-50 text-slate-500 hover:bg-slate-100"
                    }`} data-testid={`filter-type-${f.value}`}>
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-1">
                {[
                  { value: "all", label: "All Status" },
                  { value: "unpaid", label: "Unpaid" },
                  { value: "partial", label: "Partial" },
                  { value: "paid", label: "Paid" },
                ].map(f => (
                  <button key={f.value} onClick={() => setInvPaymentFilter(f.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      invPaymentFilter === f.value ? "bg-violet-100 text-violet-700" : "bg-slate-50 text-slate-500 hover:bg-slate-100"
                    }`} data-testid={`filter-status-${f.value}`}>
                    {f.label}
                  </button>
                ))}
              </div>
              <input type="month" value={invMonthFilter} onChange={e => setInvMonthFilter(e.target.value)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-600"
                data-testid="filter-month" />
              {(invTypeFilter !== "all" || invMonthFilter || invPaymentFilter !== "all") && (
                <button onClick={() => { setInvTypeFilter("all"); setInvMonthFilter(""); setInvPaymentFilter("all"); }}
                  className="text-xs text-red-500 hover:text-red-600 font-medium flex items-center gap-1"
                  data-testid="clear-filters">
                  <XCircle className="w-3 h-3" /> Clear
                </button>
              )}
            </div>

            <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="client-invoices-full">
              <div className="p-4 border-b border-slate-100">
                <h3 className="text-sm font-semibold text-slate-900">Invoices ({invoices.length})</h3>
              </div>
              {invoices.length > 0 ? (
                <div className="divide-y divide-slate-50 max-h-[600px] overflow-y-auto">
                  {invoices.map(inv => (
                    <div key={inv.invoice_id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50" data-testid={`full-inv-${inv.invoice_id}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${inv.invoice_type === "sales" ? "bg-blue-50 text-blue-600" : "bg-purple-50 text-purple-600"}`}>
                        <FileText className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{inv.customer_name || inv.vendor_name || "Unknown"}</p>
                        <p className="text-xs text-slate-500">{inv.invoice_number} &middot; {inv.invoice_date} &middot; {inv.invoice_type}</p>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusBadge(inv.payment_status)}`}>{inv.payment_status}</span>
                      <span className="text-sm font-bold text-slate-900 min-w-[80px] text-right">{fmt(inv.grand_total)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-slate-400"><FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" /><p className="text-sm">No invoices matching filters</p></div>
              )}
            </div>
          </div>
        )}

        {/* GST Tab */}
        {activeTab === "gst" && (
          <div className="space-y-4">
            {/* GST Alerts */}
            {gstAlerts.length > 0 && (
              <div className="space-y-2" data-testid="gst-alerts">
                {gstAlerts.map((a, i) => (
                  <div key={i} className={`flex items-center gap-2.5 p-3 rounded-xl border ${
                    a.type === "warning" ? "bg-amber-50 border-amber-200" : "bg-blue-50 border-blue-200"
                  }`}>
                    <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${a.type === "warning" ? "text-amber-600" : "text-blue-600"}`} />
                    <p className={`text-xs font-medium ${a.type === "warning" ? "text-amber-800" : "text-blue-800"}`}>{a.message}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="client-gst-view">
              <div className="p-4 border-b border-slate-100">
                <h3 className="text-sm font-semibold text-slate-900">GST Summary (Monthly)</h3>
              </div>
              {gstData.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50">
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500">Month</th>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500">Output GST</th>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500">Input GST</th>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500">Net GST</th>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500">Invoices</th>
                        <th className="text-center px-4 py-2.5 text-xs font-semibold text-slate-500">Filing Ready</th>
                        <th className="text-center px-4 py-2.5 text-xs font-semibold text-slate-500">Filed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {gstData.map(row => (
                        <tr key={row.month} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <span className="font-medium text-slate-900">{row.month}</span>
                            {row.alerts?.length > 0 && (
                              <p className="text-[10px] text-amber-600 mt-0.5">{row.alerts.join(", ")}</p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right text-emerald-700">{fmt(row.output_gst)}</td>
                          <td className="px-4 py-3 text-right text-blue-700">{fmt(row.input_gst)}</td>
                          <td className={`px-4 py-3 text-right font-bold ${row.net_gst >= 0 ? "text-red-600" : "text-emerald-600"}`}>{fmt(row.net_gst)}</td>
                          <td className="px-4 py-3 text-right text-slate-600">{row.invoice_count}</td>
                          <td className="px-4 py-3 text-center">
                            {row.filing_ready
                              ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" />
                              : <AlertTriangle className="w-4 h-4 text-amber-400 mx-auto" title="Missing data" />
                            }
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleMarkFiled(row.month)}
                              disabled={filingLoading === row.month}
                              className={`text-xs font-medium px-3 py-1 rounded-full transition-colors ${
                                row.filed
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                                  : "bg-slate-50 text-slate-500 border border-slate-200 hover:bg-slate-100"
                              }`}
                              data-testid={`mark-filed-${row.month}`}
                            >
                              {filingLoading === row.month ? (
                                <Loader2 className="w-3 h-3 animate-spin inline" />
                              ) : row.filed ? (
                                <>Filed</>
                              ) : (
                                <>Mark Filed</>
                              )}
                            </button>
                            {row.filed && row.filed_at && (
                              <p className="text-[9px] text-slate-400 mt-0.5">{new Date(row.filed_at).toLocaleDateString()}</p>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-slate-400"><p className="text-sm">No GST data available</p></div>
              )}
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === "documents" && (
          <div className="space-y-4" data-testid="client-documents-tab">
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900">Document Requests ({docRequests.length})</h3>
                <Button size="sm" onClick={() => setShowDocReq(true)} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs" data-testid="new-doc-request-btn">
                  <Plus className="w-3 h-3 mr-1" /> New Request
                </Button>
              </div>
              {docRequests.length > 0 ? (
                <div className="divide-y divide-slate-50">
                  {docRequests.map(req => {
                    const priorityCfg = {
                      urgent: { color: "text-red-700", bg: "bg-red-50", border: "border-red-200" },
                      high: { color: "text-orange-700", bg: "bg-orange-50", border: "border-orange-200" },
                      normal: { color: "text-slate-600", bg: "bg-slate-50", border: "border-slate-200" },
                      low: { color: "text-slate-400", bg: "bg-slate-50", border: "border-slate-100" },
                    }[req.priority || "normal"] || { color: "text-slate-600", bg: "bg-slate-50", border: "border-slate-200" };

                    const statusCfg = {
                      pending: { label: "Pending", color: "text-amber-700", bg: "bg-amber-50" },
                      submitted: { label: "Submitted", color: "text-blue-700", bg: "bg-blue-50" },
                      reviewed: { label: "Reviewed", color: "text-violet-700", bg: "bg-violet-50" },
                      overdue: { label: "Overdue", color: "text-red-700", bg: "bg-red-50" },
                      completed: { label: "Completed", color: "text-emerald-700", bg: "bg-emerald-50" },
                    }[req.status] || { label: req.status, color: "text-slate-600", bg: "bg-slate-50" };

                    return (
                      <div key={req.request_id} className="p-4" data-testid={`doc-req-${req.request_id}`}>
                        <div className="flex items-start gap-3">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            req.status === "completed" ? "bg-emerald-50 text-emerald-600" :
                            req.status === "submitted" ? "bg-blue-50 text-blue-600" :
                            req.status === "overdue" ? "bg-red-50 text-red-600" :
                            "bg-slate-100 text-slate-500"
                          }`}>
                            <FileText className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-semibold text-slate-900">{req.title}</p>
                              {req.priority && req.priority !== "normal" && (
                                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${priorityCfg.bg} ${priorityCfg.color} border ${priorityCfg.border}`}>
                                  {req.priority}
                                </span>
                              )}
                              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${statusCfg.bg} ${statusCfg.color}`}>
                                {statusCfg.label}
                              </span>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">
                              {req.month && `Month: ${req.month} | `}
                              {req.due_date && `Due: ${req.due_date} | `}
                              {req.uploaded_count || 0} file(s) uploaded
                              {req.description && ` | ${req.description}`}
                            </p>

                            {/* Uploads list */}
                            {req.uploads?.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {req.uploads.map(u => (
                                  <div key={u.upload_id} className="flex items-center gap-2 text-xs bg-slate-50 rounded-lg px-2.5 py-1.5">
                                    <FileText className="w-3 h-3 text-slate-400" />
                                    <span className="text-slate-700 truncate flex-1">{u.filename}</span>
                                    <span className="text-slate-400">{(u.size / 1024).toFixed(0)} KB</span>
                                    <a
                                      href={`${API}/ca/document-requests/${req.request_id}/uploads/${u.upload_id}`}
                                      className="text-violet-600 hover:underline"
                                      data-testid={`download-${u.upload_id}`}
                                    >
                                      <Download className="w-3 h-3" />
                                    </a>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Notes */}
                            {req.notes?.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {req.notes.map(n => (
                                  <div key={n.note_id} className="flex items-start gap-1.5 text-xs">
                                    <MessageSquare className="w-3 h-3 text-slate-400 mt-0.5 flex-shrink-0" />
                                    <span className="text-slate-600"><span className="font-medium">{n.user_name}:</span> {n.text}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Status actions */}
                          <div className="flex gap-1 flex-shrink-0">
                            {req.status === "submitted" && (
                              <Button size="sm" variant="outline" onClick={() => handleStatusUpdate(req.request_id, "reviewed")} className="text-[10px] px-2 py-1 h-auto border-violet-200 text-violet-700">
                                Reviewed
                              </Button>
                            )}
                            {(req.status === "reviewed" || req.status === "submitted") && (
                              <Button size="sm" variant="outline" onClick={() => handleStatusUpdate(req.request_id, "completed")} className="text-[10px] px-2 py-1 h-auto border-emerald-200 text-emerald-700">
                                Complete
                              </Button>
                            )}
                            {req.status === "pending" && (
                              <Button size="sm" variant="outline" onClick={() => handleStatusUpdate(req.request_id, "overdue")} className="text-[10px] px-2 py-1 h-auto border-red-200 text-red-700">
                                Mark Overdue
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-8 text-center text-slate-400">
                  <Inbox className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">No document requests yet. Create one to request files from this client.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Doc Request Modal */}
      {showDocReq && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="doc-request-modal">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">Request Documents</h2>
              <button onClick={() => setShowDocReq(false)} className="p-1 rounded-lg hover:bg-slate-100"><XCircle className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Request Title *</label>
                <Input value={docTitle} onChange={e => setDocTitle(e.target.value)} placeholder="Upload invoices for March 2026" data-testid="doc-req-title" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Description</label>
                <Textarea value={docDesc} onChange={e => setDocDesc(e.target.value)} placeholder="Details about what's needed..." rows={2} className="text-sm" data-testid="doc-req-desc" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Month</label>
                  <Input type="month" value={docMonth} onChange={e => setDocMonth(e.target.value)} data-testid="doc-req-month" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Due Date</label>
                  <Input type="date" value={docDueDate} onChange={e => setDocDueDate(e.target.value)} data-testid="doc-req-due-date" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1.5 block">Priority</label>
                <div className="flex gap-1.5">
                  {[
                    { value: "low", label: "Low", color: "text-slate-500 border-slate-200 bg-slate-50" },
                    { value: "normal", label: "Normal", color: "text-blue-600 border-blue-200 bg-blue-50" },
                    { value: "high", label: "High", color: "text-orange-600 border-orange-200 bg-orange-50" },
                    { value: "urgent", label: "Urgent", color: "text-red-600 border-red-200 bg-red-50" },
                  ].map(p => (
                    <button key={p.value} type="button" onClick={() => setDocPriority(p.value)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        docPriority === p.value ? p.color + " ring-1 ring-offset-1" : "text-slate-400 border-slate-200 bg-white"
                      }`}
                      data-testid={`doc-priority-${p.value}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-slate-100 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowDocReq(false)}>Cancel</Button>
              <Button onClick={handleDocRequest} disabled={sending} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white" data-testid="send-doc-req-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />} Send Request
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
